import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Briefcase, CheckCircle2, Loader2, XCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const { checkAuth } = useAuth();
  const [status, setStatus] = useState('checking'); // checking, success, failed

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    
    if (sessionId) {
      pollStatus(sessionId);
    } else {
      setStatus('failed');
    }
  }, [searchParams]);

  const pollStatus = async (sessionId, attempts = 0) => {
    if (attempts >= 8) { setStatus('failed'); return; }
    try {
      const { data } = await axios.get(`${API}/checkout/status/${sessionId}`, { withCredentials: true });
      if (data.payment_status === 'paid') {
        setStatus('success');
        await checkAuth();
        return;
      }
      if (data.status === 'expired') { setStatus('failed'); return; }
      setTimeout(() => pollStatus(sessionId, attempts + 1), 2000);
    } catch {
      setStatus('failed');
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center px-4">
      <div className="bg-white rounded-3xl border border-[#E5E0D8] p-12 text-center shadow-[0_8px_30px_rgb(0,0,0,0.04)] max-w-md w-full" data-testid="payment-result">
        {status === 'checking' && (
          <>
            <Loader2 size={48} className="text-[#047857] mx-auto mb-4 animate-spin" strokeWidth={1.5} />
            <h2 className="text-xl font-medium mb-2" style={{fontFamily:'Outfit'}}>Processing Payment</h2>
            <p className="text-[#1C2A24]/60">Please wait while we confirm your payment...</p>
          </>
        )}
        {status === 'success' && (
          <>
            <CheckCircle2 size={48} className="text-[#047857] mx-auto mb-4" strokeWidth={1.5} />
            <h2 className="text-xl font-medium mb-2" style={{fontFamily:'Outfit'}}>Payment Successful!</h2>
            <p className="text-[#1C2A24]/60 mb-6">Your membership has been upgraded. Welcome aboard!</p>
            <Link to="/dashboard">
              <Button className="rounded-full bg-[#047857] hover:bg-[#065F46] text-white px-8" data-testid="go-dashboard-btn">Go to Dashboard</Button>
            </Link>
          </>
        )}
        {status === 'failed' && (
          <>
            <XCircle size={48} className="text-red-500 mx-auto mb-4" strokeWidth={1.5} />
            <h2 className="text-xl font-medium mb-2" style={{fontFamily:'Outfit'}}>Payment Issue</h2>
            <p className="text-[#1C2A24]/60 mb-6">We couldn't verify your payment. Please try again or contact support.</p>
            <Link to="/membership">
              <Button className="rounded-full bg-[#047857] hover:bg-[#065F46] text-white px-8">Back to Plans</Button>
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
